import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, LinearProgress, Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PublicIcon from "@mui/icons-material/Public";
import PunchClockOutlinedIcon from "@mui/icons-material/PunchClockOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
  createSignagePlaylist,
  deleteSignageAsset,
  deleteSignagePlaylist,
  fetchSignageAssets,
  fetchYoutubeImports,
  formatBytes,
  formatClock,
  formatPlaylistRange,
  removeYoutubeImport,
  updateSignagePlaylist,
  uploadSignageAsset,
} from "./signageApi";
import YoutubeImportDialog from "./SignageYoutube";
import SignageImageEditor from "./imageEditor/SignageImageEditor";
import {
  ACCEPTED_FILES,
  AssetThumb,
  EmptyState,
  ErrorNote,
  ICON_BTN_SM,
  ICON_BTN_SM_DANGER,
  INPUT,
  LabeledInput,
  Loading,
  ScheduleChip,
  SearchInput,
  Segmented,
  StatusDot,
} from "./signageUi";

// Images bigger than this are worth a trip through the editor, which saves
// them at screen size (the TVs are 1920×1080).
const LARGE_IMAGE_BYTES = 2.5 * 1024 * 1024;

// Playlists and Media tabs. Both work on one "library" at a time: the
// company-wide one (playlists any branch's displays can play; Admin/Executive
// Team only) or one branch's. `scope` is { id, name, company?, pcwEnabled? }
// — a branch record, or the company pseudo-branch whose id the API reads as
// branch: null.

// ================= Library picker =================

export const LibraryPicker = ({ scopes, value, onChange }) => {
  const current = scopes.find((s) => s.id === value);
  return (
    <div className="app-card flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center">
      <div className="flex items-center gap-3">
        <span className="app-icon-tile h-9 w-9 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
          {current?.company ? (
            <PublicIcon fontSize="small" />
          ) : (
            <PlaylistPlayIcon fontSize="small" />
          )}
        </span>
        <span className="font-semibold">Library</span>
      </div>
      {scopes.length > 1 ? (
        <select
          className={`${INPUT} sm:w-64`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Library"
        >
          {scopes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.company ? "Company-wide (all branches)" : s.name}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-sm font-medium">{current?.name}</span>
      )}
      <p className="app-muted text-xs leading-relaxed sm:flex-1">
        {current?.company
          ? "Company-wide playlists and media can be added to displays at any branch."
          : `Playlists and media for ${current?.name || "this branch"}'s displays only.`}
      </p>
    </div>
  );
};

// ================= Playlists =================

// playlist id → the displays that play it (with their branch name).
const usageByPlaylist = (devices, branches) => {
  const branchName = new Map(branches.map((b) => [b.id, b.name]));
  const map = new Map();
  for (const device of devices || []) {
    if (device.mode === "board") continue;
    for (const id of device.playlists || []) {
      if (!map.has(id)) map.set(id, []);
      map.get(id).push({ ...device, branchName: branchName.get(device.branch) });
    }
  }
  return map;
};

export const PlaylistsPanel = ({
  scope,
  playlists,
  devices,
  branches,
  userId,
  selectedId,
  onSelect,
  onOpenDevice,
  onChanged,
}) => {
  const [assets, setAssets] = useState(null);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    setAssets(null);
    fetchSignageAssets(scope.id, userId)
      .then(setAssets)
      .catch(() => setAssets([]));
  }, [scope.id, userId]);

  const own = (playlists || []).filter((p) =>
    scope.company ? p.company : p.branch === scope.id
  );
  const usage = useMemo(
    () => usageByPlaylist(devices, branches),
    [devices, branches]
  );

  const create = async () => {
    try {
      const playlist = await createSignagePlaylist(
        scope.id,
        { name: newName, items: [] },
        userId
      );
      setNewName("");
      await onChanged();
      onSelect(playlist.id);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!playlists) return <Loading />;
  const selected = own.find((p) => p.id === selectedId) || null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
      <aside className="app-card h-fit space-y-3 rounded-2xl border p-3">
        <div className="flex gap-2">
          <input
            className={INPUT}
            placeholder="New playlist name"
            aria-label="New playlist name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newName.trim() && create()}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!newName.trim()}
            onClick={create}
            title="Create playlist"
            aria-label="Create playlist"
            sx={{ minWidth: 40, px: 1.25 }}
          >
            <AddIcon fontSize="small" />
          </Button>
        </div>
        <ErrorNote>{error}</ErrorNote>
        <div className="space-y-0.5">
          {own.map((p) => {
            const on = usage.get(p.id)?.length || 0;
            const active = p.id === selectedId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p.id)}
                aria-current={active ? "true" : undefined}
                className={`w-full rounded-xl px-3 py-2 text-left transition ${
                  active
                    ? "bg-sky-500/15 ring-1 ring-inset ring-sky-500/30"
                    : "hover:bg-[color:var(--app-hover)]"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`flex-1 truncate text-sm font-medium ${
                      active ? "text-sky-800 dark:text-sky-200" : ""
                    }`}
                  >
                    {p.name}
                  </span>
                  <ScheduleChip playlist={p} />
                </span>
                <span className="app-muted block text-xs tabular-nums">
                  {p.items.length} item{p.items.length === 1 ? "" : "s"} ·{" "}
                  {on === 0
                    ? "not on any display"
                    : `on ${on} display${on === 1 ? "" : "s"}`}
                </span>
              </button>
            );
          })}
          {own.length === 0 && (
            <p className="app-muted px-2 py-3 text-sm">
              No playlists here yet — name one above to start.
            </p>
          )}
        </div>
      </aside>

      <div>
        {selected ? (
          assets === null ? (
            <Loading />
          ) : (
            <PlaylistEditor
              key={selected.id}
              playlist={selected}
              branch={scope}
              assets={assets}
              displays={usage.get(selected.id) || []}
              userId={userId}
              onOpenDevice={onOpenDevice}
              onSaved={onChanged}
              onDeleted={() => {
                onSelect(null);
                onChanged();
              }}
            />
          )
        ) : (
          <EmptyState
            icon={<PlaylistPlayIcon />}
            title={own.length ? "Pick a playlist to edit" : "Build your first playlist"}
          >
            A playlist is an ordered set of images and videos. Build it here,
            then add it to displays from the Displays tab —{" "}
            {scope.company
              ? "company playlists work on displays at every branch."
              : "a display can play several playlists together."}
          </EmptyState>
        )}
      </div>
    </div>
  );
};

const PlaylistEditor = ({
  playlist,
  branch,
  assets,
  displays,
  userId,
  onOpenDevice,
  onSaved,
  onDeleted,
}) => {
  const [name, setName] = useState(playlist.name);
  const [items, setItems] = useState(playlist.items);
  const [startDate, setStartDate] = useState(playlist.startDate ?? "");
  const [endDate, setEndDate] = useState(playlist.endDate ?? "");
  const [weatherEnabled, setWeatherEnabled] = useState(playlist.weatherEnabled);
  const [weatherDuration, setWeatherDuration] = useState(
    playlist.weatherDuration ?? ""
  );
  const [weatherEvery, setWeatherEvery] = useState(playlist.weatherEvery ?? "");
  const [shuffle, setShuffle] = useState(playlist.shuffle !== false);
  const [pcwEnabled, setPcwEnabled] = useState(!!playlist.pcwEnabled);
  // On the Clock is one shop's (PCW / Mill) only: never on company playlists.
  const pcwAvailable = !!branch?.pcwEnabled && !branch?.company;
  const [pcwDuration, setPcwDuration] = useState(playlist.pcwDuration ?? "");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const assetById = useMemo(
    () => new Map(assets.map((a) => [a.id, a])),
    [assets]
  );
  const usedIds = new Set(items.map((i) => i.asset));
  const available = assets.filter((a) => !usedIds.has(a.id));

  const numOrEmpty = (v) => (v === "" || v == null ? "" : Number(v));
  const dirty =
    name !== playlist.name ||
    startDate !== (playlist.startDate ?? "") ||
    endDate !== (playlist.endDate ?? "") ||
    weatherEnabled !== playlist.weatherEnabled ||
    numOrEmpty(weatherDuration) !== numOrEmpty(playlist.weatherDuration) ||
    numOrEmpty(weatherEvery) !== numOrEmpty(playlist.weatherEvery) ||
    shuffle !== (playlist.shuffle !== false) ||
    pcwEnabled !== !!playlist.pcwEnabled ||
    numOrEmpty(pcwDuration) !== numOrEmpty(playlist.pcwDuration) ||
    JSON.stringify(items) !== JSON.stringify(playlist.items);

  // "≈ 45s + 2 videos per loop" — images use their duration (or the Pi's 10s
  // default), trimmed videos use end − start, untrimmed videos are unknown.
  const loopSummary = useMemo(() => {
    let seconds = 0;
    let unknownVideos = 0;
    for (const item of items) {
      const asset = assetById.get(item.asset);
      if (asset?.type === "video") {
        if (item.start != null && item.end != null && item.end > item.start) {
          seconds += item.end - item.start;
        } else {
          unknownVideos += 1;
        }
      } else {
        seconds += item.duration ?? 10;
      }
    }
    if (weatherEnabled) {
      // Once per loop, or after every N slides when there are more than N.
      const every = Number(weatherEvery) || 0;
      const shows = every && items.length > every ? Math.floor(items.length / every) : 1;
      seconds += shows * (Number(weatherDuration) || 10);
    }
    if (pcwEnabled && pcwAvailable) {
      seconds += Number(pcwDuration) || 10;
    }
    if (seconds === 0 && unknownVideos === 0) return null;
    const rounded = Math.round(seconds);
    const time =
      rounded >= 120
        ? `${Math.floor(rounded / 60)}m ${rounded % 60}s`
        : `${rounded}s`;
    return unknownVideos > 0
      ? `≈ ${time} + ${unknownVideos} video${unknownVideos > 1 ? "s" : ""} per loop`
      : `≈ ${time} per loop`;
  }, [items, assetById, weatherEnabled, weatherDuration, weatherEvery, pcwEnabled, pcwDuration, pcwAvailable]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSignagePlaylist(
        playlist.id,
        {
          name,
          startDate: startDate || null,
          endDate: endDate || null,
          weatherEnabled,
          weatherDuration: weatherDuration === "" ? null : Number(weatherDuration),
          weatherEvery: weatherEvery === "" ? null : Number(weatherEvery),
          shuffle,
          pcwEnabled: pcwEnabled && pcwAvailable,
          pcwDuration: pcwDuration === "" ? null : Number(pcwDuration),
          items: items.map((item, idx) => ({ ...item, order: idx })),
        },
        userId
      );
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        branch.company
          ? `Delete company playlist "${playlist.name}"? It will be removed from every display that plays it, at every branch.`
          : `Delete playlist "${playlist.name}"? It will be removed from every display that plays it — a display with no other playlist goes blank.`
      )
    )
      return;
    try {
      await deleteSignagePlaylist(playlist.id, userId);
      onDeleted();
    } catch (err) {
      setError(err.message);
    }
  };

  // Per-video mute (delivered to the Pi as media-config "mute").
  const videoCount = items.filter(
    (i) => assetById.get(i.asset)?.type === "video"
  ).length;
  const allMuted =
    videoCount > 0 &&
    items.every((i) => assetById.get(i.asset)?.type !== "video" || i.mute);
  const setAllMuted = (mute) =>
    setItems((prev) =>
      prev.map((i) =>
        assetById.get(i.asset)?.type === "video" ? { ...i, mute } : i
      )
    );

  const move = (idx, dir) =>
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const patchItem = (idx, patch) =>
    setItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, ...patch } : item))
    );

  const numOrNull = (v) => (v === "" ? null : Number(v));

  // Drag-to-reorder: dragging starts only from a row's grip so inputs inside
  // the row keep working normally.
  const [dragArmed, setDragArmed] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const moveTo = (from, to) =>
    setItems((prev) => {
      if (from === to || from == null || to == null) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });

  const scheduleRange = formatPlaylistRange({
    startDate: startDate || null,
    endDate: endDate || null,
  });
  const weatherNeedsLocation =
    weatherEnabled && !branch?.company && (branch?.latitude == null || branch?.longitude == null);

  return (
    <div className="space-y-4">
      {/* Header: name, status at a glance, save */}
      <div className="app-card rounded-2xl border p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="min-w-0 flex-1 rounded-xl border border-transparent bg-transparent px-2 py-1 text-xl font-semibold tracking-tight text-inherit transition hover:border-[color:var(--app-input-border)] focus:border-sky-500 focus:bg-[color:var(--app-input-bg)] focus:outline-none focus:ring-[3px] focus:ring-[color:var(--app-ring)]"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Playlist name"
          />
          <div className="flex items-center gap-2">
            {dirty ? (
              <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
              </span>
            ) : (
              <span className="app-muted flex items-center gap-1 text-xs">
                <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> Saved
              </span>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={saving || !dirty || !name.trim()}
              onClick={save}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <button
              type="button"
              title="Delete playlist"
              aria-label="Delete playlist"
              onClick={remove}
              className={`${ICON_BTN_SM_DANGER} !h-9 !w-9`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </div>
        </div>
        <div className="app-muted mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-2 text-xs tabular-nums">
          <span className="flex items-center gap-1">
            <TvOutlinedIcon sx={{ fontSize: 14 }} />
            {displays.length
              ? `On ${displays.length} display${displays.length === 1 ? "" : "s"}`
              : "Not on any display"}
          </span>
          <span className="flex items-center gap-1">
            <PlaylistPlayIcon sx={{ fontSize: 14 }} />
            {items.length} slide{items.length === 1 ? "" : "s"}
          </span>
          {loopSummary && (
            <span className="flex items-center gap-1">
              <TimerOutlinedIcon sx={{ fontSize: 14 }} />
              {loopSummary}
            </span>
          )}
          {shuffle && (
            <span className="flex items-center gap-1">
              <ShuffleIcon sx={{ fontSize: 14 }} /> Shuffled
            </span>
          )}
          {scheduleRange && (
            <span className="flex items-center gap-1">
              <EventOutlinedIcon sx={{ fontSize: 14 }} /> {scheduleRange}
            </span>
          )}
        </div>
      </div>
      <ErrorNote>{error}</ErrorNote>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        {/* ---- Slides ---- */}
        <section className="app-card h-fit overflow-hidden rounded-2xl border">
          <header className="app-divider flex flex-wrap items-center gap-2 border-b px-4 py-3">
            <h3 className="font-semibold">Slides</h3>
            {shuffle && items.length > 1 && (
              <span className="app-muted text-xs">· order is shuffled every loop</span>
            )}
            <div className="ml-auto flex items-center gap-3">
              {videoCount > 1 && (
                <button
                  type="button"
                  className="app-btn app-btn-ghost app-btn-sm app-muted !gap-1 !px-2.5"
                  onClick={() => setAllMuted(!allMuted)}
                >
                  {allMuted ? <VolumeUpIcon sx={{ fontSize: 14 }} /> : <VolumeOffIcon sx={{ fontSize: 14 }} />}
                  {allMuted ? "Unmute all" : "Mute all"}
                </button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={available.length === 0}
                title={
                  available.length === 0
                    ? assets.length === 0
                      ? "Upload media on the Media tab first"
                      : "Every file in this library is already in the playlist — upload more on the Media tab"
                    : undefined
                }
                onClick={() => setShowAdd(true)}
              >
                Add media
              </Button>
            </div>
          </header>

          {items.length === 0 ? (
            <EmptyState inset icon={<PlaylistPlayIcon />} title="No slides yet">
              {assets.length === 0
                ? "Upload images or videos on the Media tab, then add them here."
                : "Click Add media to pick images and videos from this library."}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-[color:var(--app-divider)]">
              {items.map((item, idx) => {
                const asset = assetById.get(item.asset);
                const key = `${item.asset}-${idx}`;
                return (
                  <SlideRow
                    key={key}
                    item={item}
                    asset={asset}
                    index={idx}
                    count={items.length}
                    userId={userId}
                    open={expanded.has(item.asset)}
                    onToggleOpen={() => toggleExpanded(item.asset)}
                    patch={(p) => patchItem(idx, p)}
                    numOrNull={numOrNull}
                    onMove={(dir) => move(idx, dir)}
                    onRemove={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                    draggable={dragArmed === idx}
                    onArmDrag={() => setDragArmed(idx)}
                    onDisarmDrag={() => setDragArmed(null)}
                    dropTarget={dragOver === idx && dragFrom !== idx}
                    onDragStart={() => setDragFrom(idx)}
                    onDragOver={() => setDragOver(idx)}
                    onDrop={() => {
                      moveTo(dragFrom, idx);
                      setDragFrom(null);
                      setDragOver(null);
                      setDragArmed(null);
                    }}
                    onDragEnd={() => {
                      setDragFrom(null);
                      setDragOver(null);
                      setDragArmed(null);
                    }}
                  />
                );
              })}
            </ul>
          )}
        </section>

        {/* ---- Settings ---- */}
        <aside className="space-y-4">
          <SettingCard title="Playback">
            <SwitchRow
              icon={<ShuffleIcon fontSize="small" />}
              label="Shuffle"
              hint={shuffle ? "New random order every loop" : "Plays in the order shown"}
              checked={shuffle}
              onChange={setShuffle}
            />
          </SettingCard>

          <SettingCard
            title="Schedule"
            aside={
              (startDate || endDate) && (
                <button
                  type="button"
                  className="app-link text-xs"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  Clear
                </button>
              )
            }
          >
            <div className="grid grid-cols-2 gap-2">
              <LabeledInput label="From">
                <input
                  className={INPUT}
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </LabeledInput>
              <LabeledInput label="Until">
                <input
                  className={INPUT}
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </LabeledInput>
            </div>
            <p className="app-muted text-xs">
              {startDate || endDate
                ? "Skipped outside these dates (inclusive); other playlists on the display keep playing."
                : "Plays every day. Add dates for seasonal or promo content."}
            </p>
          </SettingCard>

          <SettingCard title="Live slides">
            <SwitchRow
              icon={<WbSunnyOutlinedIcon fontSize="small" />}
              label="Local weather"
              hint={
                branch?.company
                  ? "Each display's own branch"
                  : `Current conditions for ${branch?.name || "this branch"}`
              }
              checked={weatherEnabled}
              onChange={setWeatherEnabled}
            />
            {weatherEnabled && (
              <div className="app-surface space-y-3 rounded-xl border p-3">
                <Segmented
                  fullWidth
                  label="How often"
                  value={weatherEvery === "" ? "" : "every"}
                  onChange={(v) => setWeatherEvery(v === "every" ? weatherEvery || 5 : "")}
                  options={[
                    { value: "", label: "Once per loop" },
                    { value: "every", label: "Every few slides" },
                  ]}
                />
                <div className="grid grid-cols-2 gap-2">
                  {weatherEvery !== "" && (
                    <LabeledInput label="Every (slides)">
                      <input
                        className={INPUT}
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={weatherEvery}
                        onChange={(e) =>
                          setWeatherEvery(
                            e.target.value === ""
                              ? 1
                              : Math.min(100, Math.max(1, Math.round(Number(e.target.value) || 1)))
                          )
                        }
                      />
                    </LabeledInput>
                  )}
                  <LabeledInput label="Seconds on screen">
                    <input
                      className={INPUT}
                      type="number"
                      min="1"
                      step="1"
                      placeholder="10"
                      value={weatherDuration}
                      onChange={(e) => setWeatherDuration(e.target.value)}
                    />
                  </LabeledInput>
                </div>
                {weatherNeedsLocation && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    This branch has no location yet — add its coordinates on the
                    Branches tab or the weather slide won't show.
                  </p>
                )}
              </div>
            )}

            {pcwAvailable && (
              <>
                <SwitchRow
                  icon={<PunchClockOutlinedIcon fontSize="small" />}
                  label="Shop floor — On the Clock"
                  hint="Who's punched in right now on the shop clock"
                  checked={pcwEnabled}
                  onChange={setPcwEnabled}
                />
                {pcwEnabled && (
                  <div className="app-surface rounded-xl border p-3">
                    <LabeledInput label="Seconds on screen">
                      <input
                        className={INPUT}
                        type="number"
                        min="1"
                        step="1"
                        placeholder="10"
                        value={pcwDuration}
                        onChange={(e) => setPcwDuration(e.target.value)}
                      />
                    </LabeledInput>
                  </div>
                )}
              </>
            )}
          </SettingCard>

          <SettingCard title="Playing on">
            {displays.length === 0 ? (
              <p className="app-muted text-sm">
                Not on any display yet — add it from the Displays tab.
              </p>
            ) : (
              <ul className="space-y-1">
                {displays.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onOpenDevice(d.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-[color:var(--app-hover)]"
                      title="Open this display"
                    >
                      <StatusDot device={d} />
                      <span className="min-w-0 flex-1 truncate">{d.name}</span>
                      {branch?.company && d.branchName && (
                        <span className="app-muted truncate text-xs">{d.branchName}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </SettingCard>

          <p className="app-muted px-1 text-xs leading-relaxed">
            Save sends your changes to the displays — they pick them up on
            their next check-in.
          </p>
        </aside>
      </div>

      {showAdd && (
        <AddAssetsDialog
          available={available}
          userId={userId}
          onClose={() => setShowAdd(false)}
          onAdd={(ids) => {
            setItems((prev) => [
              ...prev,
              ...ids.map((id, i) => ({
                asset: id,
                order: prev.length + i,
                duration: null,
                start: null,
                end: null,
                startDate: null,
                endDate: null,
                mute: false,
              })),
            ]);
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
};

// ---- playlist editor pieces ----

const SettingCard = ({ title, aside, children }) => (
  <section className="app-card space-y-3 rounded-2xl border p-4">
    <div className="flex items-center justify-between">
      <h3 className="app-eyebrow">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

const SwitchRow = ({ icon, label, hint, checked, onChange }) => (
  <label className="flex cursor-pointer items-center gap-3">
    <span
      className={`app-icon-tile h-8 w-8 rounded-lg transition ${
        checked ? "bg-sky-500/15 text-sky-600 dark:text-sky-300" : "app-muted bg-slate-500/10"
      }`}
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium">{label}</span>
      <span className="app-muted block text-xs">{hint}</span>
    </span>
    <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </label>
);

const clock = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const shortDay = (value) => {
  const [y, m, d] = String(value).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// One slide: a single compact line (grip, thumbnail, name + summary, the
// most-used control, expand, remove), with trim/dates tucked into "More".
const SlideRow = ({
  item,
  asset,
  index,
  count,
  userId,
  open,
  onToggleOpen,
  patch,
  numOrNull,
  onMove,
  onRemove,
  draggable,
  onArmDrag,
  onDisarmDrag,
  dropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const isVideo = asset?.type === "video";
  const trimmed = item.start != null || item.end != null;
  const summary = [
    isVideo ? "Video" : asset ? "Image" : "Missing file",
    isVideo && (item.mute ? "muted" : "sound on"),
    isVideo &&
      (trimmed
        ? `plays ${clock(item.start ?? 0)}–${item.end != null ? clock(item.end) : "end"}`
        : "full length"),
    item.startDate && `from ${shortDay(item.startDate)}`,
    item.endDate && `until ${shortDay(item.endDate)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
      className={`transition ${dropTarget ? "bg-sky-500/10 shadow-[inset_0_2px_0_#0ea5e9]" : ""}`}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          className="app-muted -ml-1 flex cursor-grab flex-col items-center rounded-lg px-0.5 py-1 hover:bg-[color:var(--app-hover)] active:cursor-grabbing"
          title="Drag to reorder"
          aria-label={`Slide ${index + 1} — drag to reorder`}
          onPointerDown={onArmDrag}
          onPointerUp={onDisarmDrag}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
          <span className="text-[10px] font-semibold tabular-nums">{index + 1}</span>
        </button>
        <div className="w-24 shrink-0">
          {asset ? (
            <AssetThumb asset={asset} userId={userId} sizeClass="h-[54px] w-24" />
          ) : (
            <div className="app-muted flex h-[54px] w-24 items-center justify-center rounded-lg bg-slate-500/10 text-[10px]">
              missing
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium" title={asset?.filename}>
            {asset?.filename ?? "(missing file)"}
          </p>
          <p className="app-muted truncate text-xs">{summary}</p>
        </div>

        {isVideo ? (
          <button
            type="button"
            onClick={() => patch({ mute: !item.mute })}
            aria-pressed={!!item.mute}
            title={item.mute ? "Muted — click to play with sound" : "Plays with sound — click to mute"}
            aria-label={item.mute ? "Muted — click to play with sound" : "Plays with sound — click to mute"}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition ${
              item.mute
                ? "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                : "app-input hover:border-sky-400"
            }`}
          >
            {item.mute ? <VolumeOffIcon sx={{ fontSize: 17 }} /> : <VolumeUpIcon sx={{ fontSize: 17 }} />}
          </button>
        ) : (
          <label className="flex shrink-0 items-center gap-1" title="Seconds on screen">
            <input
              className="app-input w-14 rounded-lg border px-2 py-1 text-right text-sm tabular-nums"
              aria-label="Seconds on screen"
              type="number"
              min="1"
              step="1"
              placeholder="10"
              value={item.duration ?? ""}
              onChange={(e) => patch({ duration: numOrNull(e.target.value) })}
            />
            <span className="app-muted text-xs">s</span>
          </label>
        )}

        <button
          type="button"
          onClick={onToggleOpen}
          title={open ? "Fewer options" : "More options"}
          aria-label={open ? "Fewer options" : "More options"}
          aria-expanded={open}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition hover:bg-[color:var(--app-hover)] ${
            open || trimmed || item.startDate || item.endDate ? "app-accent" : "app-muted"
          }`}
        >
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </button>
        <button
          type="button"
          onClick={onRemove}
          title="Remove from playlist"
          aria-label="Remove from playlist"
          className={ICON_BTN_SM_DANGER}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      {open && (
        <div className="app-surface mx-3 mb-3 grid grid-cols-2 gap-3 rounded-xl border p-3 sm:grid-cols-4">
          {isVideo && (
            <>
              <LabeledInput label="Start at (s)">
                <input
                  className={INPUT}
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="0"
                  value={item.start ?? ""}
                  onChange={(e) => patch({ start: numOrNull(e.target.value) })}
                />
              </LabeledInput>
              <LabeledInput label="Stop at (s)">
                <input
                  className={INPUT}
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="end"
                  value={item.end ?? ""}
                  onChange={(e) => patch({ end: numOrNull(e.target.value) })}
                />
              </LabeledInput>
            </>
          )}
          <LabeledInput label="Show from">
            <input
              className={INPUT}
              type="date"
              value={item.startDate ?? ""}
              onChange={(e) => patch({ startDate: e.target.value || null })}
            />
          </LabeledInput>
          <LabeledInput label="Show until">
            <input
              className={INPUT}
              type="date"
              value={item.endDate ?? ""}
              onChange={(e) => patch({ endDate: e.target.value || null })}
            />
          </LabeledInput>
          <div className="col-span-2 flex flex-wrap items-center gap-2 sm:col-span-4">
            <Button size="small" startIcon={<ArrowUpwardIcon />} disabled={index === 0} onClick={() => onMove(-1)}>
              Move up
            </Button>
            <Button size="small" startIcon={<ArrowDownwardIcon />} disabled={index === count - 1} onClick={() => onMove(1)}>
              Move down
            </Button>
            <span className="app-muted ml-auto text-xs">
              Dates hide just this slide; blank = always.
            </span>
          </div>
        </div>
      )}
    </li>
  );
};

// Pick library files to add to a playlist: search, filter by type, select
// several (or all shown), and add them in one go.
const AddAssetsDialog = ({ available, userId, onClose, onAdd }) => {
  const [selected, setSelected] = useState([]); // ids, in click order
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const query = search.trim().toLowerCase();
  const shown = available
    .filter((a) => type === "all" || a.type === type)
    .filter((a) => !query || a.filename.toLowerCase().includes(query))
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  const allShownSelected = shown.length > 0 && shown.every((a) => selected.includes(a.id));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <div className="flex max-h-[88vh] flex-col">
        <div className="app-divider flex items-center gap-3 border-b px-5 py-4">
          <span className="app-icon-tile h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
            <PlaylistPlayIcon fontSize="small" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold tracking-tight">Add media to playlist</h3>
            <p className="app-muted text-xs">
              Click files to select them — they're added in the order you pick them.
            </p>
          </div>
          <button
            type="button"
            className="app-icon-btn -mr-2"
            title="Close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="app-divider flex flex-col gap-2 border-b px-5 py-3 sm:flex-row sm:items-center">
          <SearchInput
            className="flex-1"
            placeholder="Search by file name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <Segmented
            label="Type"
            value={type}
            onChange={setType}
            options={[
              { value: "all", label: "All" },
              { value: "image", label: "Images" },
              { value: "video", label: "Videos" },
            ]}
          />
          {shown.length > 1 && (
            <button
              type="button"
              className="app-link shrink-0 text-xs"
              onClick={() =>
                setSelected((prev) =>
                  allShownSelected
                    ? prev.filter((id) => !shown.some((a) => a.id === id))
                    : [...prev, ...shown.map((a) => a.id).filter((id) => !prev.includes(id))]
                )
              }
            >
              {allShownSelected ? "Deselect these" : `Select all ${shown.length}`}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {shown.length === 0 ? (
            <EmptyState inset icon={<SearchOffIcon />}>
              {available.length === 0
                ? "Everything in this library is already in the playlist."
                : "No files match."}
            </EmptyState>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {shown.map((asset) => {
                const order = selected.indexOf(asset.id);
                const isSelected = order >= 0;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => toggle(asset.id)}
                    aria-pressed={isSelected}
                    className={`app-card relative overflow-hidden rounded-2xl border p-1.5 text-left transition ${
                      isSelected ? "ring-2 ring-sky-500" : "hover:border-sky-400"
                    }`}
                  >
                    <AssetThumb asset={asset} userId={userId} sizeClass="aspect-video w-full" />
                    <span
                      className={`absolute left-3.5 top-3.5 flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs font-bold shadow-sm ${
                        isSelected
                          ? "border-sky-500 bg-sky-500 text-white"
                          : "border-white/90 bg-black/30 text-transparent"
                      }`}
                    >
                      {isSelected ? order + 1 : ""}
                    </span>
                    {asset.type === "video" && (
                      <span className="absolute right-3.5 top-3.5 flex items-center rounded-md bg-black/70 px-1.5 py-0.5 text-white">
                        <VideocamOutlinedIcon sx={{ fontSize: 14 }} />
                      </span>
                    )}
                    <div className="px-1.5 pb-1 pt-2">
                      <p className="truncate text-xs font-medium" title={asset.filename}>
                        {asset.filename}
                      </p>
                      <p className="app-muted text-[11px] tabular-nums">
                        {asset.type} · {formatBytes(asset.sizeBytes)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="app-divider flex items-center gap-3 border-t px-5 py-3">
          <span className="app-muted flex-1 text-xs">
            {selected.length ? `${selected.length} selected` : "Nothing selected yet"}
            {selected.length > 0 && (
              <button type="button" className="app-link ml-2" onClick={() => setSelected([])}>
                Clear
              </button>
            )}
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={selected.length === 0}
            onClick={() => onAdd(selected)}
          >
            {selected.length ? `Add ${selected.length} to playlist` : "Add to playlist"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};


// ================= Media Library =================

const MEDIA_FILTERS = [
  { key: "all", label: "All" },
  { key: "image", label: "Images" },
  { key: "video", label: "Videos" },
  { key: "unused", label: "Unused" },
];
const MEDIA_SORTS = [
  { key: "newest", label: "Newest first" },
  { key: "name", label: "Name A–Z" },
  { key: "size", label: "Largest first" },
];

export const MediaPanel = ({ scope, playlists, userId, onChanged }) => {
  const fileRef = useRef(null);
  const [assets, setAssets] = useState(null);
  const [error, setError] = useState(null);
  const [upload, setUpload] = useState(null); // { name, index, total } while busy
  const [dragDepth, setDragDepth] = useState(0); // >0 while files hover the tab
  const [showYoutube, setShowYoutube] = useState(false);
  const [preview, setPreview] = useState(null); // { asset, url }
  const [editing, setEditing] = useState(null); // asset open in the image editor
  const [imports, setImports] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(() => new Set());
  const finishedIds = useRef(new Set());

  const load = () => {
    fetchSignageAssets(scope.id, userId)
      .then((rows) => {
        setAssets(rows);
        setError(null);
        // Drop selections for files that no longer exist.
        setSelected((prev) => new Set([...prev].filter((id) => rows.some((a) => a.id === id))));
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    setAssets(null);
    setSelected(new Set());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.id, userId]);

  // YouTube imports run on the server; poll while any are in flight and
  // reload the library the moment one finishes.
  const loadImports = () =>
    fetchYoutubeImports(scope.id, userId)
      .then((rows) => {
        const newlyDone = rows.filter(
          (j) => j.status === "done" && !finishedIds.current.has(j.id)
        );
        rows
          .filter((j) => j.status === "done")
          .forEach((j) => finishedIds.current.add(j.id));
        if (newlyDone.length) load();
        setImports(rows);
      })
      .catch(() => {});

  useEffect(() => {
    finishedIds.current = new Set();
    setImports([]);
    loadImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.id, userId]);

  const importsActive = imports.some((j) =>
    ["queued", "downloading"].includes(j.status)
  );
  useEffect(() => {
    if (!importsActive) return;
    const timer = setInterval(loadImports, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importsActive, scope.id, userId]);

  const dismissImport = async (job) => {
    if (
      ["queued", "downloading"].includes(job.status) &&
      !window.confirm(`Stop importing "${job.title}"?`)
    )
      return;
    try {
      await removeYoutubeImport(job.id, userId);
    } catch (err) {
      setError(err.message);
    }
    loadImports();
  };

  // asset id → names of the playlists (in this library) that use it.
  const usedIn = useMemo(() => {
    const map = new Map();
    for (const p of playlists || []) {
      for (const item of p.items) {
        if (!map.has(item.asset)) map.set(item.asset, []);
        map.get(item.asset).push(p.name);
      }
    }
    return map;
  }, [playlists]);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length || upload) return;
    setError(null);
    try {
      // Sequential so a per-file error message stays attributable.
      for (let i = 0; i < list.length; i++) {
        setUpload({ name: list[i].name, index: i + 1, total: list.length });
        await uploadSignageAsset(scope.id, list[i], userId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpload(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    }
  };

  const deleteAssets = async (targets) => {
    if (!targets.length) return;
    const affected = [...new Set(targets.flatMap((a) => usedIn.get(a.id) || []))];
    const what =
      targets.length === 1 ? `"${targets[0].filename}"` : `these ${targets.length} files`;
    const warning = affected.length
      ? `Delete ${what}? ${targets.length === 1 ? "It" : "They"} will also be removed from: ${affected.join(", ")}.`
      : `Delete ${what}?`;
    if (!window.confirm(warning)) return;
    try {
      for (const a of targets) await deleteSignageAsset(a.id, userId);
    } catch (err) {
      setError(err.message);
    }
    setSelected(new Set());
    load();
    if (affected.length) onChanged();
  };

  const toggleSelected = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ---- filtering / sorting ----
  const query = search.trim().toLowerCase();
  const shown = (assets || [])
    .filter((a) => {
      if (filter === "image" || filter === "video") return a.type === filter;
      if (filter === "unused") return !(usedIn.get(a.id) || []).length;
      return true;
    })
    .filter(
      (a) =>
        !query ||
        a.filename.toLowerCase().includes(query) ||
        (a.youtube?.title || "").toLowerCase().includes(query)
    )
    .sort((a, b) =>
      sort === "name"
        ? a.filename.localeCompare(b.filename)
        : sort === "size"
          ? (b.sizeBytes || 0) - (a.sizeBytes || 0)
          : String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
    );

  const stats = useMemo(() => {
    const list = assets || [];
    return {
      images: list.filter((a) => a.type === "image").length,
      videos: list.filter((a) => a.type === "video").length,
      unused: list.filter((a) => !(usedIn.get(a.id) || []).length).length,
      bytes: list.reduce((sum, a) => sum + (a.sizeBytes || 0), 0),
    };
  }, [assets, usedIn]);

  const selectedAssets = (assets || []).filter((a) => selected.has(a.id));
  const selecting = selected.size > 0;
  const libraryName = scope.company ? "the company library" : `${scope.name}'s library`;

  // Files dragged anywhere over the tab (not just a drop box) upload here.
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const dropProps = {
    onDragEnter: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setDragDepth((d) => d + 1);
    },
    onDragOver: (e) => isFileDrag(e) && e.preventDefault(),
    onDragLeave: (e) => isFileDrag(e) && setDragDepth((d) => Math.max(0, d - 1)),
    onDrop: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setDragDepth(0);
      onFiles(e.dataTransfer.files);
    },
  };

  const activity = [
    ...(upload
      ? [
          {
            id: "upload",
            icon: <FileUploadOutlinedIcon className="app-accent" />,
            title: upload.name,
            status:
              upload.total > 1 ? `Uploading ${upload.index} of ${upload.total}…` : "Uploading…",
            progress: null,
          },
        ]
      : []),
    ...imports.map((job) => ({
      id: job.id,
      job,
      icon: <YouTubeIcon sx={{ color: "#ff0000" }} />,
      title: `${job.title} (${formatClock(job.start)}–${formatClock(job.end)})`,
    })),
  ];

  return (
    <div className="relative space-y-4" {...dropProps}>
      {dragDepth > 0 && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-sky-500 bg-sky-50/90 dark:bg-sky-950/85">
          <div className="text-center">
            <CloudUploadOutlinedIcon className="app-accent" sx={{ fontSize: 48 }} />
            <p className="mt-1 text-lg font-semibold">Drop to upload to {libraryName}</p>
          </div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPTED_FILES}
        className="hidden"
        onChange={(e) => onFiles(e.target.files)}
      />

      {/* Toolbar */}
      <div className="app-card space-y-4 rounded-2xl border p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <p className="font-semibold tabular-nums">
              {assets ? `${assets.length} file${assets.length === 1 ? "" : "s"}` : "Media"}
            </p>
            {assets && assets.length > 0 && (
              <p className="app-muted text-xs tabular-nums">
                {stats.images} image{stats.images === 1 ? "" : "s"} · {stats.videos} video
                {stats.videos === 1 ? "" : "s"} · {formatBytes(stats.bytes)}
                {stats.unused > 0 && ` · ${stats.unused} unused`}
              </p>
            )}
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              variant="contained"
              startIcon={<FileUploadOutlinedIcon />}
              disabled={!!upload}
              onClick={() => fileRef.current?.click()}
            >
              Upload
            </Button>
            <Button
              variant="outlined"
              startIcon={<YouTubeIcon sx={{ color: "#ff0000" }} />}
              onClick={() => setShowYoutube(true)}
            >
              From YouTube
            </Button>
          </div>
        </div>

        {assets && assets.length > 0 && (
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <SearchInput
              className="flex-1"
              placeholder="Search by file name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Segmented
              label="Show"
              value={filter}
              onChange={setFilter}
              options={MEDIA_FILTERS.map((f) => ({ value: f.key, label: f.label }))}
            />
            <select
              className={`${INPUT} md:w-40`}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort"
            >
              {MEDIA_SORTS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <ErrorNote>{error}</ErrorNote>

      {/* Uploads + YouTube imports in progress */}
      {activity.length > 0 && (
        <div className="app-card divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-2xl border">
          {activity.map((row) => {
            const job = row.job;
            const running = !job || ["queued", "downloading"].includes(job.status);
            return (
              <div key={row.id} className="flex items-center gap-3 px-4 py-3">
                {row.icon}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium" title={row.title}>
                    {row.title}
                  </p>
                  {job?.status === "failed" ? (
                    <p className="text-xs text-red-600 dark:text-red-400">Import failed: {job.error}</p>
                  ) : job?.status === "done" ? (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400">
                      Added as {job.filename}
                    </p>
                  ) : (
                    <div className="mt-1 flex items-center gap-2">
                      <LinearProgress
                        className="flex-1"
                        variant={!job || job.status === "queued" ? "indeterminate" : "determinate"}
                        value={job?.progress ?? 0}
                      />
                      <span className="app-muted w-32 text-right text-xs">
                        {!job
                          ? row.status
                          : job.status === "queued"
                            ? "Waiting…"
                            : `Importing ${job.progress}%`}
                      </span>
                    </div>
                  )}
                </div>
                {job && (
                  <button
                    type="button"
                    className="app-btn app-btn-ghost app-btn-sm app-muted hover:!text-rose-600 dark:hover:!text-rose-400"
                    onClick={() => dismissImport(job)}
                  >
                    {running ? "Cancel" : "Dismiss"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk actions */}
      {selecting && (
        <div className="app-glass sticky top-[4.75rem] z-20 flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-2.5">
          <span className="text-sm font-semibold tabular-nums">
            {selected.size} selected
          </span>
          <button
            type="button"
            className="app-link text-xs"
            onClick={() => setSelected(new Set(shown.map((a) => a.id)))}
          >
            Select all {shown.length}
          </button>
          <div className="ml-auto flex gap-2">
            <Button size="small" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => deleteAssets(selectedAssets)}
            >
              Delete
            </Button>
          </div>
        </div>
      )}

      {!assets ? (
        <Loading />
      ) : assets.length === 0 ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="app-divider flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed px-6 py-14 text-center transition hover:border-sky-400 hover:bg-sky-500/5"
        >
          <span className="app-icon-tile mb-2 h-14 w-14 rounded-2xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
            <CloudUploadOutlinedIcon />
          </span>
          <p className="font-semibold">Drop images or videos here, or click to browse</p>
          <p className="app-muted max-w-md text-sm leading-relaxed">
            They go into {libraryName} and become available to its playlists.
            jpg, png, gif, bmp, webp, mp4, mkv, mov, avi — the TVs are 1920×1080
            landscape.
          </p>
        </button>
      ) : shown.length === 0 ? (
        <EmptyState icon={<SearchOffIcon />} title="Nothing matches">
          {filter === "unused"
            ? "Every file here is in at least one playlist."
            : "Try a different search or filter."}
        </EmptyState>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {shown.map((asset) => (
            <MediaCard
              key={asset.id}
              asset={asset}
              userId={userId}
              usedIn={usedIn.get(asset.id) || []}
              selected={selected.has(asset.id)}
              selecting={selecting}
              onToggleSelect={() => toggleSelected(asset.id)}
              onOpen={(url) => setPreview({ asset, url })}
              onEdit={asset.type === "image" ? () => setEditing(asset) : null}
              onDelete={() => deleteAssets([asset])}
            />
          ))}
        </div>
      )}

      <p className="app-muted text-xs">
        Tip: drag files anywhere onto this tab to upload them. Click a
        thumbnail to preview it; images can be resized, cropped, and given
        text in the editor.
      </p>

      {preview && (
        <AssetPreviewDialog
          asset={preview.asset}
          url={preview.url}
          usedIn={usedIn.get(preview.asset.id) || []}
          onClose={() => setPreview(null)}
          onEdit={
            preview.asset.type === "image"
              ? () => {
                  setEditing(preview.asset);
                  setPreview(null);
                }
              : null
          }
        />
      )}

      {editing && (
        <SignageImageEditor
          asset={editing}
          scope={scope}
          libraryImages={(assets || []).filter((a) => a.type === "image")}
          userId={userId}
          onClose={() => setEditing(null)}
          onLibraryChanged={load}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {showYoutube && (
        <YoutubeImportDialog
          scope={scope}
          userId={userId}
          onClose={() => setShowYoutube(false)}
          onQueued={() => {
            setShowYoutube(false);
            loadImports();
          }}
        />
      )}
    </div>
  );
};

// One library file: 16:9 thumbnail with badges and a select box, name, and
// where it's used. Actions show on hover (always on touch screens).
const MediaCard = ({
  asset,
  userId,
  usedIn,
  selected,
  selecting,
  onToggleSelect,
  onOpen,
  onEdit,
  onDelete,
}) => {
  const large = asset.type === "image" && asset.sizeBytes > LARGE_IMAGE_BYTES;
  return (
    <div
      className={`app-card group relative overflow-hidden rounded-2xl border transition ${
        selected ? "ring-2 ring-sky-500" : ""
      }`}
    >
      <div className="relative p-1.5">
        <AssetThumb
          asset={asset}
          userId={userId}
          sizeClass="aspect-video w-full"
          onOpen={(url) => (selecting ? onToggleSelect() : onOpen(url))}
        />
        {/* Select */}
        <button
          type="button"
          onClick={onToggleSelect}
          title={selected ? "Deselect" : "Select"}
          aria-label={`${selected ? "Deselect" : "Select"} ${asset.filename}`}
          aria-pressed={selected}
          className={`absolute left-3.5 top-3.5 flex h-6 w-6 items-center justify-center rounded-full border-2 shadow-sm transition ${
            selected
              ? "border-sky-500 bg-sky-500 text-white"
              : `border-white/90 bg-black/30 text-transparent hover:bg-black/50 ${
                  selecting ? "" : "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                }`
          }`}
        >
          <CheckIcon sx={{ fontSize: 16 }} />
        </button>
        {/* Badges */}
        <div className="pointer-events-none absolute right-3.5 top-3.5 flex gap-1">
          {asset.youtube && (
            <span className="flex items-center rounded-md bg-black/70 px-1.5 py-0.5 text-white" title="From YouTube">
              <YouTubeIcon sx={{ fontSize: 14, color: "#ff4d4d" }} />
            </span>
          )}
          {asset.edited && (
            <span className="flex items-center rounded-md bg-black/70 px-1.5 py-0.5 text-white" title="Edited in the image editor">
              <AutoFixHighOutlinedIcon sx={{ fontSize: 13 }} />
            </span>
          )}
          {asset.type === "video" && (
            <span className="flex items-center rounded-md bg-black/70 px-1.5 py-0.5 text-white" title="Video">
              <VideocamOutlinedIcon sx={{ fontSize: 14 }} />
            </span>
          )}
        </div>
        {large && (
          <span
            className="pointer-events-none absolute bottom-3.5 left-3.5 flex items-center gap-0.5 rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
            title="Larger than the TVs need — open it in the editor and save to shrink it"
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 12 }} /> Large
          </span>
        )}
      </div>

      <div className="px-3 pb-2.5 pt-1">
        <p className="truncate text-sm font-medium" title={asset.youtube ? `From YouTube: ${asset.youtube.title}` : asset.filename}>
          {asset.filename}
        </p>
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-xs tabular-nums ${usedIn.length ? "app-muted" : "text-amber-700 dark:text-amber-400"}`}
            title={usedIn.length ? `In: ${usedIn.join(", ")}` : "Not in any playlist"}
          >
            {usedIn.length
              ? usedIn.length === 1
                ? usedIn[0]
                : `${usedIn.length} playlists`
              : "Unused"}
            <span className="app-muted"> · {formatBytes(asset.sizeBytes)}</span>
          </span>
          <span className="flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100 [@media(hover:none)]:opacity-100">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                title="Edit image"
                aria-label={`Edit ${asset.filename}`}
                className={ICON_BTN_SM}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </button>
            )}
            <button
              type="button"
              onClick={onDelete}
              title="Delete"
              aria-label={`Delete ${asset.filename}`}
              className={ICON_BTN_SM_DANGER}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

// Full-size look at one library file — the image, or the video with
// controls (sound on, so you can hear what the TVs would play).
const AssetPreviewDialog = ({ asset, url, usedIn, onClose, onEdit }) => (
  <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
    <div className="flex items-center gap-2 px-5 py-3">
      {asset.youtube && <YouTubeIcon sx={{ color: "#ff0000" }} />}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold" title={asset.filename}>
          {asset.filename}
        </p>
        <p className="app-muted truncate text-xs">
          {asset.type} · {formatBytes(asset.sizeBytes)}
          {asset.createdAt
            ? ` · added ${new Date(asset.createdAt).toLocaleDateString()}`
            : ""}
          {asset.youtube
            ? ` · from YouTube: ${asset.youtube.title} (${formatClock(asset.youtube.start)}–${formatClock(asset.youtube.end)})`
            : ""}
        </p>
      </div>
      {onEdit && (
        <Button size="small" variant="outlined" onClick={onEdit}>
          Edit image
        </Button>
      )}
      <a
        href={url}
        download={asset.filename}
        className="app-icon-btn"
        title="Download"
        aria-label="Download"
      >
        <DownloadIcon fontSize="small" />
      </a>
      <button
        type="button"
        className="app-icon-btn -mr-2"
        title="Close"
        aria-label="Close"
        onClick={onClose}
      >
        <CloseIcon fontSize="small" />
      </button>
    </div>
    <div className="flex items-center justify-center bg-black">
      {asset.type === "video" ? (
        <video
          src={url}
          controls
          autoPlay
          className="max-h-[75vh] w-full object-contain"
        />
      ) : (
        <img
          src={url}
          alt={asset.filename}
          className="max-h-[75vh] w-full object-contain"
        />
      )}
    </div>
    <p className="app-muted px-5 py-3 text-xs">
      {usedIn.length
        ? `In playlists: ${usedIn.join(", ")}`
        : "Not in any playlist yet."}
    </p>
  </Dialog>
);
